import { z } from 'zod';

/**
 * Horizontal position of a dot on the hill chart.
 *
 * - 0   = left foot of the hill (we're starting to dig, unknown territory)
 * - 50  = summit (we understand the problem, we know where we're going)
 * - 100 = right foot (done, shipped)
 *
 * Stored as a float so the UI can animate smoothly during drag. Integer
 * snapping is a UI concern, not a domain concern.
 */
export const hillPositionSchema = z.number().min(0).max(100);
export type HillPosition = z.infer<typeof hillPositionSchema>;

/**
 * Default palette for dots.
 *
 * Bound to the PMS (Patrick Mathieu Singularité) 2018 brand charter p.6 —
 * the six "couleur noyaux" representing the singularité archetypes, plus
 * an amber accent to widen the rotation so milestones in dense projects
 * don't collide after six imports.
 *
 * The index-rotation logic in the hill creation path (i % DOT_COLORS.length)
 * gives each freshly imported milestone a distinct color without any
 * collision-avoidance algorithm.
 */
export const DOT_COLORS = [
  '#312783', // pms violet — souverain magicien
  '#009fe3', // pms blue   — souverain
  '#009640', // pms green  — producteur réinventeur
  '#efd500', // pms yellow — producteur séducteur
  '#e30613', // pms red    — guerrier cosmique
  '#e6007e', // pms magenta — guerrier humain
  '#f59e0b', // amber      — warm accent beyond the six
] as const;

export type DotColor = (typeof DOT_COLORS)[number];

/**
 * Hex color validator for the `color` field of a dot.
 *
 * Accepts any 6-digit hex (`#rrggbb`). We do NOT enforce membership in
 * DOT_COLORS so a future "custom color" feature doesn't require a schema
 * breaking change.
 */
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex value, e.g. "#f59e0b".');
